import React, { useCallback, useEffect, useState } from 'react';
import { Box, makeStyles, useMediaQuery } from '@material-ui/core';
import VenueResult from './VenueResult';
import CuratedCarousel from '../../components/core/Carousel/curatedCarousel';
import NoResults from './NoResults';
import { useHistory } from 'react-router-dom';
import CircularLoader from '../../components/core/Loader/CircularLoader';
import { useScrollContextRef } from '../../components/search/helpers';
import InfiniteScroll from 'react-infinite-scroller';
import CuratedPickInfoBox from './CuratedPickInfoBox';
import { CURATED_PICKS_TYPE } from '../../utils/constants/CuratedPickTypes';
import TableBookingModal, {
  STEPS,
} from '../../components/spoon/TableBookingModal';
import theme from '../../theme';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(0, 'auto', 10),
    height: '100%',
    width: '100%',
    maxWidth: '100vw',
  },
  venueResultsContainer: {
    margin: theme.spacing(0, 'auto'),
    maxWidth: 1115,
    minHeight: 'calc(100vh - 240px)',
    [theme.breakpoints.up('xxl')]: {
      minHeight: 'calc(100vh - 236px)',
    },
  },
  venueResultsCardsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    rowGap: theme.spacing(3),
    columnGap: theme.spacing(2),
  },
  venueResultCard: {
    verticalAlign: 'top',
    boxSizing: 'border-box',
    display: 'flex',
    maxWidth: 361,
    width: '100%',
  },
  infoBoxContainer: {
    margin: theme.spacing(1.25, 0),
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& *': {
      boxSizing: 'border-box',
      flexGrow: 0,
      maxWidth: '361px',
      [theme.breakpoints.up('lg')]: {
        maxWidth: '738px',
        width: '738px',
      },
    },
  },
}));

const SearchContent = ({
  venues = [],
  availableCuratedPicks = [],
  activeCuratedPick,
  resetAllSearch,
  handleCuratedPickSelect,
  failedVenueIds = [],
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { stepUp } = useSelector((state) => state.reservations);
  const [stepOverride, setStepOverride] = useState(
    stepUp?.shouldRedirect && stepUp?.isModal
      ? STEPS.CONFIRM_RESERVATION
      : STEPS.CHOOSE_DATE
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingModalVenue, setBookingModalVenue] = useState(null);
  const openBookingModal = (venue) => {
    setBookingModalVenue(venue);
    setBookingModalOpen(true);
  };

  const handleVenueSelect = useCallback(
    (id) => {
      history.push(`/venue/${id}`, { isSearchResult: true });
    },
    [history]
  );

  const venuesPerPage = 12;
  const scrollRef = useScrollContextRef();
  const [hasMore, setHasMore] = useState(true);
  const [visibleVenues, setVisibleVenues] = useState([]);
  const [index, setIndex] = useState(venuesPerPage);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    setHasMore(true);
    setVisibleVenues([]);
    setIndex(venuesPerPage);
  }, [venues]);
  const fetchVenues = useCallback(async () => {
    if (fetching) {
      return;
    }
    setFetching(true);
    try {
      var nextVenues = [];
      for (let i = index - venuesPerPage; i < index; i++) {
        if (i >= venues.length) {
          setHasMore(false);
          break;
        }
        nextVenues.push(venues[i]);
      }
      setVisibleVenues([...visibleVenues, ...nextVenues]);
      setIndex(index + venuesPerPage);
    } finally {
      setFetching(false);
    }
  }, [visibleVenues, venues, fetching, index]);

  if (!venues || venues.length === 0) {
    return <NoResults clearSearch={resetAllSearch} />;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.venueResultsContainer}>
        {availableCuratedPicks.length > 0 && (
          <>
            <CuratedCarousel
              curatedLabels={availableCuratedPicks}
              activeLabel={activeCuratedPick}
              onLabelSelection={handleCuratedPickSelect}
            />
            {[
              CURATED_PICKS_TYPE.SIGNATURE_COLLECTION,
              CURATED_PICKS_TYPE.EVENTS,
            ].includes(activeCuratedPick) && (
              <Box className={classes.infoBoxContainer}>
                <CuratedPickInfoBox type={activeCuratedPick} />
              </Box>
            )}
          </>
        )}
        {venues.length === 0 ? (
          <NoResults clearSearch={resetAllSearch} />
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={fetchVenues}
            hasMore={hasMore}
            loader={<CircularLoader />}
            useWindow={false}
            getScrollParent={() => scrollRef.current}>
            <Box className={classes.venueResultsCardsContainer}>
              {visibleVenues?.map((venue, index) => {
                return (
                  <Box
                    key={`${venue.id}_VenueResultBox_${index}`}
                    className={classes.venueResultCard}>
                    <VenueResult
                      venue={venue}
                      onVenueSelect={handleVenueSelect}
                      index={index}
                      bookingModalOpen={bookingModalOpen}
                      setBookingModalOpen={setBookingModalOpen}
                      bookingModalVenue={bookingModalVenue}
                      setBookingModalVenue={setBookingModalVenue}
                      openBookingModal={() => openBookingModal(venue)}
                      stepOverride={stepOverride}
                      setStepOverride={setStepOverride}
                      showAvailabilityError={
                        failedVenueIds?.length > 0 &&
                        failedVenueIds.includes(venue.sevenRoomsVenueId)
                      }
                    />
                  </Box>
                );
              })}
            </Box>
          </InfiniteScroll>
        )}
      </Box>
      {bookingModalOpen && (
        <TableBookingModal
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
          venue={bookingModalVenue}
          isDrawer={isMobile}
          stepOverride={stepOverride}
        />
      )}
    </Box>
  );
};

export default SearchContent;
